/* src/components/customGame/setup/panels/SequenceRulesPanel.js */
import React from 'react';
import PropTypes from 'prop-types';
import Accordion from '../../ui/Accordion';

// Re-using the SubSettingsSection helper
const SettingsSection = ({ title, children, helpText = "", className = "" }) => (
    <div className={`mb-6 p-4 border border-gray-200 rounded-md shadow-sm bg-white ${className}`}>
        <h3 className="text-lg font-medium text-gray-800 mb-3">{title}</h3>
        {helpText && <p className="text-sm text-gray-500 mb-3 -mt-2">{helpText}</p>}
        <div className="space-y-4">
            {children}
        </div>
    </div>
);

SettingsSection.propTypes = {
    title: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired,
    helpText: PropTypes.string,
    className: PropTypes.string,
};

const LURE_TYPES_AVAILABLE = [
    { key: "N-1 Lure", label: "N-1 Lure (Matches item N-1 trials ago)" },
    { key: "N+1 Lure", label: "N+1 Lure (Matches item N+1 trials ago)" },
    { key: "N-2 Lure", label: "N-2 Lure (Matches item N-2 trials ago)" },
    { key: "N+2 Lure", label: "N+2 Lure (Matches item N+2 trials ago)" },
    { key: "Repetition Lure", label: "Repetition Lure (Matches item N=0 trials ago)" },
    // { key: "Similarity Lure", label: "Similarity Lure (Perceptually similar non-target)" }, // Requires similarity features active
];


const SequenceRulesPanel = ({ config, onConfigChange, pathPrefix }) => {
    // Ensure config and its nested properties exist to prevent errors
    const matchesConfig = config?.Matches || {};
    const luresConfig = config?.Lures || {};
    const overlapConfig = config?.Overlap || {};
    const constraintsConfig = config?.Constraints || {};
    // const structureConfig = config?.Structure || {}; // For Algorithm later

    const handleChange = (section, key, value) => {
        onConfigChange(`${pathPrefix}.${section}.${key}`, value);
    };
    
    // Special handler for Lures.Types (array of strings)
    const handleLureTypeChange = (lureTypeKey, isChecked) => {
        const currentLureTypes = luresConfig.Types || [];
        let newLureTypes;
        if (isChecked) {
            newLureTypes = [...currentLureTypes, lureTypeKey];
        } else {
            newLureTypes = currentLureTypes.filter(type => type !== lureTypeKey);
        }
        handleChange('Lures', 'Types', newLureTypes);
    };

    return (
        <div className="p-6 space-y-6 bg-gray-50 min-h-full">

            <SettingsSection title="Match Generation" helpText="Control the frequency and distribution of target matches.">
                <div>
                    <label htmlFor="matchMode" className="block text-sm font-medium text-gray-700 mb-1">
                        Match Frequency Mode
                    </label>
                    <select
                        id="matchMode"
                        className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                        value={matchesConfig.Mode || 'Target Percentage'}
                        onChange={(e) => handleChange('Matches', 'Mode', e.target.value)}
                    >
                        <option value="Target Percentage">Target Percentage</option>
                        <option value="Fixed Count">Fixed Count</option>
                    </select>
                </div>

                {matchesConfig.Mode === 'Target Percentage' && (
                    <div>
                        <label htmlFor="matchTargetPercentage" className="block text-sm font-medium text-gray-700 mb-1">
                            Target Match Percentage (0.0 to 1.0)
                        </label>
                        <input
                            type="number"
                            id="matchTargetPercentage"
                            className="mt-1 block w-full pl-3 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                            value={matchesConfig.TargetPercentage !== undefined ? matchesConfig.TargetPercentage : 0.25}
                            onChange={(e) => handleChange('Matches', 'TargetPercentage', parseFloat(e.target.value))}
                            min="0" max="1" step="0.01"
                        />
                    </div>
                )}
                {matchesConfig.Mode === 'Fixed Count' && (
                     <div>
                        <label htmlFor="matchFixedCount" className="block text-sm font-medium text-gray-700 mb-1">
                            Fixed Number of Matches
                        </label>
                        <input
                            type="number"
                            id="matchFixedCount"
                            className="mt-1 block w-full pl-3 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                            value={matchesConfig.FixedCount || 15}
                            onChange={(e) => handleChange('Matches', 'FixedCount', parseInt(e.target.value, 10))}
                            min="0" step="1"
                        />
                    </div>
                )}
                {/* Min/Max Distance for matches - can be in an accordion */}
                <Accordion title="Match Spacing (Advanced)" id="match-spacing-accordion" defaultOpen={false}>
                    <div className="space-y-3 p-3 bg-gray-50 rounded-b-md border border-t-0 border-gray-200">
                        <div>
                            <label htmlFor="matchMinDistance" className="block text-sm font-medium text-gray-700 mb-1">
                                Minimum Trials Between Matches
                            </label>
                            <input
                                type="number"
                                id="matchMinDistance"
                                className="mt-1 block w-full pl-3 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                value={matchesConfig.MinDistance !== undefined ? matchesConfig.MinDistance : 1}
                                onChange={(e) => handleChange('Matches', 'MinDistance', parseInt(e.target.value, 10))}
                                min="0" max="10" step="1"
                            />
                        </div>
                        <div>
                            <label htmlFor="matchMaxDistance" className="block text-sm font-medium text-gray-700 mb-1">
                                Maximum Trials Between Matches (0 for no max)
                            </label>
                            <input
                                type="number"
                                id="matchMaxDistance"
                                className="mt-1 block w-full pl-3 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                value={matchesConfig.MaxDistance !== undefined ? matchesConfig.MaxDistance : 10}
                                onChange={(e) => handleChange('Matches', 'MaxDistance', parseInt(e.target.value, 10))}
                                min="0" max="50" step="1"
                            />
                        </div>
                    </div>
                </Accordion>
                {/* Match Distribution settings (Equal, Weighted, Per Stimulus) can be added later */}
            </SettingsSection>


            <SettingsSection title="Lure Generation (Interference Trials)" helpText="Configure non-target trials designed to elicit false alarms.">
                <div className="flex items-center">
                    <input
                        id="luresEnabled"
                        type="checkbox"
                        className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                        checked={luresConfig.Enabled !== undefined ? luresConfig.Enabled : true}
                        onChange={(e) => handleChange('Lures', 'Enabled', e.target.checked)}
                    />
                    <label htmlFor="luresEnabled" className="ml-2 block text-sm font-medium text-gray-700">
                        Enable Lure Trials
                    </label>
                </div>

                {luresConfig.Enabled && (
                    <div className="pl-2 mt-3 space-y-4 border-l-2 border-gray-200 ml-2">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Lure Types to Include:
                            </label>
                            <div className="space-y-2">
                                {LURE_TYPES_AVAILABLE.map(lure => (
                                    <div key={lure.key} className="flex items-center">
                                        <input
                                            id={`lure-type-${lure.key}`}
                                            type="checkbox"
                                            className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                                            checked={(luresConfig.Types || []).includes(lure.key)}
                                            onChange={(e) => handleLureTypeChange(lure.key, e.target.checked)}
                                        />
                                        <label htmlFor={`lure-type-${lure.key}`} className="ml-2 block text-sm text-gray-600">
                                            {lure.label}
                                        </label>
                                    </div>
                                ))}
                                 {/* Placeholder for Similarity Lure if similarity features are active */}
                                {/* <div><input type="checkbox" disabled /> Similarity Lure (Enable in Stimulus Settings)</div> */}
                            </div>
                        </div>

                        <div>
                            <label htmlFor="lureMode" className="block text-sm font-medium text-gray-700 mb-1">
                                Lure Frequency Mode
                            </label>
                            <select
                                id="lureMode"
                                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                                value={luresConfig.Mode || 'Target Percentage (Overall)'}
                                onChange={(e) => handleChange('Lures', 'Mode', e.target.value)}
                            >
                                <option value="Target Percentage (Overall)">Target Percentage (Overall Lures)</option>
                                {/* <option value="Fixed Count (Overall)">Fixed Count (Overall Lures)</option> */}
                                {/* <option value="Per Lure Type">Target Per Lure Type (Advanced)</option> */}
                            </select>
                        </div>
                        {luresConfig.Mode === 'Target Percentage (Overall)' && (
                            <div>
                                <label htmlFor="lureTargetPercentageOverall" className="block text-sm font-medium text-gray-700 mb-1">
                                    Overall Lure Percentage (0.0 to 1.0, relative to non-match trials or total trials)
                                </label>
                                <input
                                    type="number"
                                    id="lureTargetPercentageOverall"
                                    className="mt-1 block w-full pl-3 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                    value={luresConfig.TargetPercentage?.Overall !== undefined ? luresConfig.TargetPercentage.Overall : 0.15}
                                    onChange={(e) => handleChange('Lures', 'TargetPercentage', { ...luresConfig.TargetPercentage, Overall: parseFloat(e.target.value) })}
                                    min="0" max="1" step="0.01"
                                />
                            </div>
                        )}
                         {/* Lure Distribution settings can be added later */}
                    </div>
                )}
            </SettingsSection>

             <SettingsSection title="Overlap Control (Multi-modal Matches)" helpText="Configure simultaneous matches across multiple active modalities.">
                <div className="flex items-center">
                    <input
                        id="overlapControlEnabled"
                        type="checkbox"
                        className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                        checked={overlapConfig.ControlEnabled !== undefined ? overlapConfig.ControlEnabled : true}
                        onChange={(e) => handleChange('Overlap', 'ControlEnabled', e.target.checked)}
                    />
                    <label htmlFor="overlapControlEnabled" className="ml-2 block text-sm font-medium text-gray-700">
                        Enable Overlap Control
                    </label>
                </div>
                {overlapConfig.ControlEnabled && (
                     <div className="pl-2 mt-3 space-y-4 border-l-2 border-gray-200 ml-2">
                        <div>
                            <label htmlFor="overlapTargetPercentage" className="block text-sm font-medium text-gray-700 mb-1">
                                Target Overlap Percentage (0.0 to 1.0, e.g., of total trials)
                            </label>
                            <input
                                type="number"
                                id="overlapTargetPercentage"
                                className="mt-1 block w-full pl-3 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                value={overlapConfig.TargetPercentage !== undefined ? overlapConfig.TargetPercentage : 0.05}
                                onChange={(e) => handleChange('Overlap', 'TargetPercentage', parseFloat(e.target.value))}
                                min="0" max="1" step="0.01"
                            />
                        </div>
                        {/* AllowedCombinations will be complex, likely a multi-select checklist of modality pairs/triples based on enabled stimuli - for later */}
                        <p className="text-xs text-gray-500">Specifying allowed combinations for overlap will be available later.</p>
                    </div>
                )}
            </SettingsSection>

            <SettingsSection title="Sequence Constraints & Structure">
                 <div className="flex items-center">
                    <input
                        id="preventImmediateRepeats"
                        type="checkbox"
                        className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                        checked={constraintsConfig.PreventImmediateRepeats !== undefined ? constraintsConfig.PreventImmediateRepeats : true}
                        onChange={(e) => handleChange('Constraints', 'PreventImmediateRepeats', e.target.checked)}
                    />
                    <label htmlFor="preventImmediateRepeats" className="ml-2 block text-sm font-medium text-gray-700">
                        Prevent Immediate Repeats (Stimulus t != Stimulus t-1, unless N=1 match)
                    </label>
                </div>
                {/* MinNonMatchRun and Sequence.Structure.Algorithm can be added later or in an accordion */}
            </SettingsSection>
        </div>
    );
};

SequenceRulesPanel.propTypes = {
    config: PropTypes.shape({
        Matches: PropTypes.object,
        Lures: PropTypes.object,
        Overlap: PropTypes.object,
        Constraints: PropTypes.object,
        Structure: PropTypes.object,
    }).isRequired,
    onConfigChange: PropTypes.func.isRequired,
    pathPrefix: PropTypes.string.isRequired, // e.g., "Sequence"
};

export default SequenceRulesPanel;